import { HttpClientModule } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import * as fromSharedState from '@boma/administration-portal/shared/state';
import {
  UiStyleEffects,
  UserEffects,
} from '@boma/administration-portal/shared/state';
import { APP_CONFIG } from '@boma/app-config';
import { NotificationsModule } from '@boma/libs/shared/modules';
import { AuthGuard } from '@boma/shared/common';
import { UiModule } from '@boma/ui';
import { EffectsModule } from '@ngrx/effects';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { ActionReducer, StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { OAuthModule } from 'angular-oauth2-oidc';
import { localStorageSync } from 'ngrx-store-localstorage';
import { environment } from '../environments/environment';
import { AppComponent } from './app.component';

export function localStorageSyncReducer(
  reducer: ActionReducer<any>
): ActionReducer<any> {
  return localStorageSync({
    keys: [fromSharedState.UISTYLE_FEATURE_KEY],
    rehydrate: true,
  })(reducer);
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    provideFirebaseApp(() => initializeApp(environment.firebaseConfig)),
    BrowserModule,
    BrowserAnimationsModule,
    UiModule,
    HttpClientModule,
    RouterModule.forRoot(
      [
        {
          path: '',
          loadChildren: () =>
            import('@boma/administration-portal/account').then(
              (module) => module.AdministrationPortalAccountModule
            ),
          canActivate: [AuthGuard],
        },
        {
          path: 'dashboard',
          loadChildren: () =>
            import('@boma/administration-portal/dashboard').then(
              (module) => module.AdministrationPortalDashboardModule
            ),
          canActivate: [AuthGuard],
        },
        {
          path: 'crm',
          loadChildren: () =>
            import('@boma/administration-portal/crm').then(
              (module) => module.AdministrationPortalCrmModule
            ),
          canActivate: [AuthGuard],
        },
        {
          path: 'users',
          loadChildren: () =>
            import('@boma/administration-portal/users').then(
              (module) => module.AdministrationPortalUsersModule
            ),
          canActivate: [AuthGuard],
        },
        {
          path: 'products',
          loadChildren: () =>
            import('@boma/administration-portal/products').then(
              (module) => module.AdministrationPortalProductsModule
            ),
          canActivate: [AuthGuard],
        },
        {
          path: 'locations',
          loadChildren: () =>
            import('@boma/administration-portal/locations').then(
              (module) => module.AdministrationPortalLocationsModule
            ),
          canActivate: [AuthGuard],
        },
        {
          path: 'settings',
          loadChildren: () =>
            import('@boma/administration-portal/settings').then(
              (module) => module.AdministrationPortalSettingsModule
            ),
          canActivate: [AuthGuard],
        },
        {
          path: 'account',
          loadChildren: () =>
            import('@boma/administration-portal/account').then(
              (module) => module.AdministrationPortalAccountModule
            ),
          canActivate: [AuthGuard],
        },
        {
          path: 'login',
          loadChildren: () =>
            import('@boma/administration-portal/account').then(
              (module) => module.AdministrationPortalAccountModule
            ),
        },
        { path: '**', redirectTo: 'dashboard' },
      ],
      {
        initialNavigation: 'enabledNonBlocking',
        scrollPositionRestoration: 'top',
      }
    ),
    OAuthModule.forRoot({
      resourceServer: {
        allowedUrls: [environment.apiRoute],
        sendAccessToken: true,
      },
    }),
    StoreModule.forRoot(
      {
        [fromSharedState.UISTYLE_FEATURE_KEY]: fromSharedState.reducer,
        [fromSharedState.USER_FEATURE_KEY]: fromSharedState.userReducer,
      },
      {
        metaReducers: [localStorageSyncReducer],
        runtimeChecks: {
          strictActionImmutability: true,
          strictStateImmutability: true,
        },
      }
    ),
    EffectsModule.forRoot([UiStyleEffects, UserEffects]),
    !environment.production ? StoreDevtoolsModule.instrument() : [],
    StoreRouterConnectingModule.forRoot(),
    NotificationsModule,
  ],
  providers: [{ provide: APP_CONFIG, useValue: environment }, AuthGuard],
  bootstrap: [AppComponent],
})
export class AppModule {}
